import { useEffect, useMemo, useState } from "react";

export const PRIORITY_RANK = { high: 3, medium: 2, low: 1 };

export const isPendingStatus = (s) => typeof s === "string" && s.startsWith("pending_");
export const isClosedStatus = (s) => s === "rejected" || s === "cancelled";

export const requestTotal = (r) => {
  const n = Number(r.quantity) * Number(r.unit_price);
  return Number.isFinite(n) ? n : 0;
};

// Bekleyen talep, son durum değişikliğinden (yoksa oluşturulmadan) beri kaç gündür bekliyor.
export const waitingDays = (r) => {
  const d = new Date(r.updated_at || r.created_at);
  if (Number.isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

const dayKey = (v) => {
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const ts = (v) => {
  const n = new Date(v).getTime();
  return Number.isNaN(n) ? 0 : n;
};

const SORTERS = {
  newest: (a, b) => ts(b.created_at) - ts(a.created_at),
  oldest: (a, b) => ts(a.created_at) - ts(b.created_at),
  priority: (a, b) =>
    (PRIORITY_RANK[b.priority] || 0) - (PRIORITY_RANK[a.priority] || 0) ||
    ts(a.created_at) - ts(b.created_at),
  priceDesc: (a, b) => requestTotal(b) - requestTotal(a),
  priceAsc: (a, b) => requestTotal(a) - requestTotal(b),
};

const emptyFilters = {
  search: "",
  category: "",
  priority: "",
  status: "",
  from: "",
  to: "",
};

// Sekme filtresinden geçmiş `items` üzerinde arama/filtre/sıralama/sayfalama yapar.
// Filtre veya sekme değişince sayfa 1'e döner.
export const useSatListFilters = (items, { tabKey, pageSizeKey, defaultSort = "newest" }) => {
  const [filters, setFilters] = useState(emptyFilters);
  const [sort, setSort] = useState(defaultSort);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem(pageSizeKey), 10);
      return [10, 20, 30].includes(v) ? v : 10;
    } catch {
      return 10;
    }
  });

  const setFilter = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));
  const reset = () => setFilters(emptyFilters);
  const hasActive = Object.values(filters).some(Boolean);

  const filtered = useMemo(() => {
    const q = filters.search.trim().toLocaleLowerCase("tr-TR");
    const list = items.filter((r) => {
      if (filters.category && r.category !== filters.category) return false;
      if (filters.priority && r.priority !== filters.priority) return false;
      if (filters.status && r.status !== filters.status) return false;
      const created = dayKey(r.created_at);
      if (filters.from && created && created < filters.from) return false;
      if (filters.to && created && created > filters.to) return false;
      if (q) {
        const hay = `${r.title || ""} ${r.request_code || ""} ${r.created_by_name || ""} ${
          r.created_by_surname || ""
        }`.toLocaleLowerCase("tr-TR");
        if (!hay.includes(q)) return false;
      }
      return true;
    });
    return [...list].sort(SORTERS[sort] || SORTERS.newest);
  }, [items, filters, sort]);

  useEffect(() => {
    setPage(1);
  }, [filters, sort, tabKey]);

  useEffect(() => {
    try {
      localStorage.setItem(pageSizeKey, String(pageSize));
    } catch {
      // yoksay
    }
    setPage(1);
  }, [pageSize, pageSizeKey]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    if (page > pages) setPage(pages);
  }, [filtered.length, page, pageSize]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  return {
    filters,
    setFilter,
    reset,
    hasActive,
    sort,
    setSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    filtered,
    paged,
  };
};
